import tw from "tailwind-styled-components";
import { FaCoins, FaRupeeSign, FaBed, FaMoneyBill, FaBuilding } from "react-icons/fa";
import { IoIosArrowForward } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import Tooltip from "../Tooltip";

const CollectionSummaryCard = ({ clientSummary, currentPropertySummary, expenseStats, canViewFinance }) => {
    const navigate = useNavigate();

    const refund = Number(currentPropertySummary?.curMonthRefunded || 0);
    const expense = Number(expenseStats?.curMonthExpense || 0);
    const collection = Number(currentPropertySummary?.curMonthCollection || 0);
    const curMonthAdvancePaid = Number(currentPropertySummary?.curMonthAdvancePaid || 0);

    const incomeTillNow = collection - expense - refund;

    const pendingRent = expenseStats?.rentPending || 0; //(expenseStats?.totalLandlordRentToBePaid || 0) - (expenseStats?.curMonthRent || 0);

    return (
        <MainContainer>
            <div className="overflow-x-auto scrollbar-hide pb-2">
                <div className="flex gap-6 min-w-max">
                    {/* <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"> */}
                    {/* Collection */}
                    <Card>
                        <Header>
                            <IconWrapper><FaCoins size={20} className="text-gray-700" /></IconWrapper>
                            <Title>{'Collection'}</Title>
                        </Header>
                        <SubtitleValue className='cursor-pointer' onClick={() => navigate('/finance/collections')}>
                            <h1 className={`text-3xl font-semibold text-blue-600 tracking-tight`}>₹{currentPropertySummary?.curMonthCollection}</h1>
                            <Tooltip label="Today's collection">
                                <span className="text-sm text-gray-400 font-medium mb-1">
                                    ({'₹' + currentPropertySummary?.todayCollection?.todayCollection || 0})
                                </span>
                            </Tooltip>
                        </SubtitleValue>
                        <Subtitle className='group cursor-pointer' onClick={() => navigate('/finance/collections')}>
                            {'Collection'}
                            <Arrow />
                            {/* <IoIosArrowForward
                            size={12}
                            className="ml-1 mt-0.5"
                        /> */}
                        </Subtitle>
                        <StatsGrid>
                            <StatItem>
                                <StatNumber>₹{currentPropertySummary?.expectedRent}</StatNumber>
                                <StatLabel>
                                    {'Expected'}
                                </StatLabel>
                            </StatItem>
                            <StatItem className='group cursor-pointer' onClick={() => navigate('/finance/dues')}>
                                <StatNumber className='text-rose-500'>₹{currentPropertySummary?.dues?.curMonthDues}</StatNumber>
                                <StatLabel>
                                    {'Dues Till Now'}
                                    <Arrow />
                                    {/* <IoIosArrowForward
                                    size={12}
                                    className="ml-1 mt-0.5"
                                /> */}
                                </StatLabel>
                            </StatItem>
                        </StatsGrid>
                        <FooterText className="flex justify-between">
                            <span>
                                Security in hand: ₹{clientSummary?.securityInHand || 0}
                            </span>
                            {curMonthAdvancePaid ?
                                <span>
                                    <Tooltip label="Rent for the current month that was collected in previous months as advance payment.">
                                        Advance: ₹{curMonthAdvancePaid || 0}
                                    </Tooltip>
                                </span>
                                : ""
                            }
                        </FooterText>
                    </Card>

                    {/* Expense */}
                    <Card>
                        <Header>
                            <IconWrapper><FaRupeeSign size={20} className="text-gray-500" /></IconWrapper>
                            <Title>{'Operating Exp'}</Title>
                        </Header>
                        <SubtitleValue className='cursor-pointer' onClick={() => navigate('/finance/expenses')}>
                            <h1 className={`text-3xl font-semibold text-orange-600 tracking-tight`}>₹{expenseStats?.curMonthExpense}</h1>
                            <Tooltip label="Rent paid to landlord">
                                <span className="text-sm text-gray-400 font-medium mb-1">
                                    ({'₹' + expenseStats?.curMonthRent})
                                </span>
                            </Tooltip>
                        </SubtitleValue>

                        <Subtitle className='group cursor-pointer' onClick={() => navigate('/finance/expenses')}>
                            {'Expenses'}
                            <Arrow />
                            {/* <IoIosArrowForward
                            size={12}
                            className="ml-1 mt-0.5"
                        /> */}
                        </Subtitle>
                        <StatsGrid>
                            <StatItem className='group cursor-pointer' onClick={() => navigate('/payable-rent',
                                {
                                    state: { totalRent: expenseStats?.totalLandlordRentToBePaid }
                                })}>
                                <StatNumber>₹{expenseStats?.totalLandlordRentToBePaid}</StatNumber>
                                <StatLabel>
                                    {'Payable Rent'}
                                    <Arrow />
                                    {/* <IoIosArrowForward
                                    size={12}
                                    className="ml-1 mt-0.5"
                                /> */}
                                </StatLabel>
                            </StatItem>
                            <StatItem className='group cursor-pointer' onClick={() => navigate('/landlord/pending-payouts')}>
                                <StatNumber className='text-orange-400'>₹{pendingRent}</StatNumber>
                                <StatLabel>
                                    {'Pending Rent'}
                                    <Arrow />
                                    {/* <IoIosArrowForward
                                    size={12}
                                    className="ml-1 mt-0.5"
                                /> */}
                                </StatLabel>
                            </StatItem>
                        </StatsGrid>
                        <FooterText>{"Security paid to landlord : ₹" + expenseStats?.lifetimeSecurityPaid}</FooterText>
                    </Card>

                    {/* Income & Summary */}
                    <Card>
                        <Header>
                            <IconWrapper><FaMoneyBill size={20} className="text-gray-500" /></IconWrapper>
                            <Title>{'P&L Summary'}</Title>
                        </Header>
                        <SubtitleValue>
                            <h1 className={`text-3xl font-semibold ${incomeTillNow > 0 ? 'text-green-600' : 'text-rose-600'} tracking-tight`}>₹{incomeTillNow}</h1>
                        </SubtitleValue>

                        <Subtitle>
                            {'Till Now'}
                        </Subtitle>
                        <StatsGrid>
                            <StatItem>
                                <StatNumber>₹{currentPropertySummary?.estimatedIncome || 0}</StatNumber>
                                <StatLabel>
                                    {'Estimated Income'}
                                </StatLabel>
                            </StatItem>
                            <StatItem className='group cursor-pointer' onClick={() => navigate('/finance/refund')}>
                                <StatNumber>{'₹' + refund}</StatNumber>
                                <StatLabel>
                                    {'Refund'}
                                    <Arrow />
                                    {/* <IoIosArrowForward
                                    size={12}
                                    className="ml-1 mt-0.5"
                                /> */}
                                </StatLabel>
                            </StatItem>
                        </StatsGrid>
                        <FooterText
                            className={`group flex transition-colors ${canViewFinance
                                ? 'cursor-pointer text-blue-500 hover:text-blue-700'
                                : 'cursor-not-allowed text-gray-400'
                                }`}
                            onClick={() => {
                                if (!canViewFinance) return;
                                navigate("/income-expense");
                            }}
                        >
                            {"Month-on-Month Summary"}
                            {canViewFinance &&
                                <Arrow />
                            }
                        </FooterText>

                    </Card>

                    {/* Asset Investments */}
                    <Card>
                        <Header>
                            <IconWrapper><FaBuilding size={20} className="text-gray-500" /></IconWrapper>
                            <Title>{'Asset Investments'}</Title>
                        </Header>
                        <SubtitleValue>
                            <h1 className={`text-3xl font-semibold text-violet-600 tracking-tight`}>₹{expenseStats?.curFYAssetExpense || 0}</h1>
                        </SubtitleValue>

                        <Subtitle>
                            {'Total Investment'}
                        </Subtitle>
                        <StatsGrid>
                            <StatItem>
                                <StatNumber>₹{expenseStats?.curMonthAssetExpense || 0}</StatNumber>
                                <StatLabel>
                                    {'This Month'}
                                </StatLabel>
                            </StatItem>
                            <StatItem >
                                <StatNumber>₹{expenseStats?.totalAssetExpense || 0}</StatNumber>
                                <StatLabel>
                                    {'Current FY'}
                                    {/* <Arrow /> */}
                                </StatLabel>
                            </StatItem>
                        </StatsGrid>
                        <FooterText
                            className={'group flex transition-colors text-gray-400'}
                            // onClick={() => {
                            //     if (!canViewFinance) return;
                            //     navigate("/income-expense");
                            // }}
                        >
                            Date filter does not affect these values.

                            {/* {"Total Investment Entries"}
                            {canViewFinance &&
                                <Arrow />
                            } */}
                        </FooterText>

                    </Card>
                </div>
            </div>
        </MainContainer >
    );
};

// Styled Components
const MainContainer = tw.div`mt-6`;
// const Card = tw.div`bg-white shadow-md rounded-lg px-6 pt-6 pb-4 gap-4`;
//const MainContainer = tw.div`mt-6 overflow-x-auto scrollbar-hide`;
// const Card = tw.div`bg-white shadow-md rounded-lg px-6 pt-6 gap-4 min-w-[280px] sm:min-w-[320px] lg:min-w-[360px]`;
// const Card = tw.div`
//   bg-white rounded-xl
//   px-6 pt-6 pb-4
//   shadow-[0_6px_20px_rgba(0,0,0,0.06)]
//   hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)]
//   transition-shadow duration-200
// `;
// const Card = tw.div`
//   bg-white rounded-xl
//   px-6 pt-5 pb-4
//   shadow-[0_4px_14px_rgba(0,0,0,0.06)]
//   transition-shadow duration-200
// `;
const Card = tw.div`
  w-[360px]
  shrink-0
  bg-white
  rounded-xl
  px-6
  pt-5
  pb-4
  shadow-[0_4px_14px_rgba(0,0,0,0.06)]
  transition-shadow duration-200
`;
// const Header = tw.div`flex items-center space-x-3 mb-4`;
const Header = tw.div`
  flex items-center gap-3
`;
//const IconWrapper = tw.div`p-3 border border-gray-100 rounded-lg`;
const IconWrapper = tw.div`
  p-2 rounded-lg bg-gray-50 text-gray-500
`;
//const Title = tw.h3`text-lg font-semibold text-gray-800`;
const Title = tw.h3`
  text-sm font-semibold text-gray-700 uppercase tracking-wide
`;
//const SubtitleValue = tw.div`flex items-end gap-2 mt-6`;
const SubtitleValue = tw.div`
  flex items-baseline gap-2 mt-4
`;
const Subtitle = tw.span`text-sm text-gray-500 flex items-center ${props => props.clickable ? 'cursor-pointer' : ''}`;
//const StatsGrid = tw.div`grid md:grid-cols-2 grid-cols-1 mt-6 gap-6`;
const StatsGrid = tw.div`
  flex
  justify-between
  items-start
  mt-6
  gap-8
`;
const StatItem = tw.div`${props => props.clickable ? `cursor-pointer ` : ''}`;
//const StatNumber = tw.h4`text-lg font-semibold text-gray-800`;
//const StatLabel = tw.span`text-sm text-gray-500 flex items-center ${props => props.clickable ? 'cursor-pointer' : ''}`;
const StatNumber = tw.h4`
  text-base font-medium text-gray-800
`;

const StatLabel = tw.span`
  text-sm text-gray-500 flex items-center ${props => props.clickable ? 'cursor-pointer' : ''}`;

const Arrow = tw(IoIosArrowForward)`
  text-gray-400 text-xs
  opacity-0 group-hover:opacity-100
  transition-opacity ml-1 mt-0.5
`;

//const FooterText = tw.p`text-xs text-gray-400 font-normal mt-6 mb-4`;
const FooterText = tw.p`
  text-xs text-gray-400 mt-5 pt-3 border-t border-gray-100
`;

export default CollectionSummaryCard;
